import React, { useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { generateOHIFUrl } from '../../services/utils';

const NotificationListener = ({ userId, onNotificationReceived, onSyncComplete }) => {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Use refs for callbacks to avoid socket teardown/reconnect on every parent render
  const onNotificationReceivedRef = useRef(onNotificationReceived);
  const onSyncCompleteRef = useRef(onSyncComplete);
  const recentNotifsRef = useRef(new Map());
  useEffect(() => { onNotificationReceivedRef.current = onNotificationReceived; }, [onNotificationReceived]);
  useEffect(() => { onSyncCompleteRef.current = onSyncComplete; }, [onSyncComplete]);

  // Memoize the notification handler to prevent recreating socket
  const handleNotification = useCallback((data) => {
    if (!data) return;
    console.log('Notification received:', data);

    // Deduplicate rapid duplicate notifications (e.g. from multiple socket events)
    const notifKey = data._id || `${data.type || 'notif'}-${data.reportId || data.studyUID || data.studyId || ''}-${data.patientName || ''}`;
    const now = Date.now();
    if (recentNotifsRef.current.has(notifKey)) {
      const lastTime = recentNotifsRef.current.get(notifKey);
      if (now - lastTime < 5000) {
        console.log('Suppressed duplicate notification:', notifKey);
        return;
      }
    }
    recentNotifsRef.current.set(notifKey, now);
    if (recentNotifsRef.current.size > 50) {
      for (const [k, t] of recentNotifsRef.current.entries()) {
        if (now - t > 30000) recentNotifsRef.current.delete(k);
      }
    }
    
    // Suppress self-notifications (never toast the user for their own action)
    const senderId = (data.senderId || data.sender?._id || data.sender)?.toString();
    const currentUserId = (userId?._id || userId?.id || userId)?.toString();
    if (senderId && currentUserId && senderId === currentUserId) {
      console.log('Suppressed self-notification:', notifKey);
      return;
    }
    
    // Determine toast type based on priority and flag status
    const isFlagged = data.type === 'report_flagged' || data.isFlagged;
    const isResolved = data.type === 'report_flagged_resolved' || data.isFlaggedResolved;
    const isUrgent = isFlagged || data.priority === 'stat';
    const facilityName = data.facilityName || 'Hospital';

    let titleText = '🔔 Notification';
    if (isFlagged) {
      titleText = `🚩 Concerns Flagged by ${facilityName}!`;
    } else if (isResolved) {
      titleText = `✅ Revised Report Approved & Ready!`;
    } else if (isUrgent) {
      titleText = `🚨 STAT (within 1 hr) Report Ready!`;
    }
    
    // Play subtle alert sound for stat or flagged reports
    if ((isUrgent || isFlagged) && typeof window !== 'undefined' && window.Audio) {
      try {
        // Create a subtle notification sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = isFlagged ? 650 : (isResolved ? 900 : 800);
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      } catch (e) {
        console.warn('Could not play notification sound:', e);
      }
    }

    // Create toast content with action button
    const toastContent = (
      <div style={{ wordBreak: 'break-word' }}>
        <strong style={{ display: 'block', marginBottom: '8px', fontSize: '15px', color: isFlagged ? '#EF4444' : (isResolved ? '#10B981' : 'inherit') }}>
          {titleText}
        </strong>
        {data.message && (
          <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: isFlagged ? 600 : 400, color: isFlagged ? '#DC2626' : 'inherit' }}>
            {data.message.replace(/\^/g, ' ')}
          </p>
        )}
        {data.concerns && (
          <div style={{ margin: '6px 0', padding: '6px 10px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '4px', borderLeft: '3px solid #EF4444', fontSize: '13px' }}>
            <span style={{ fontWeight: 600, color: '#EF4444' }}>{facilityName} notes: </span>
            <span>"{data.concerns}"</span>
          </div>
        )}
        {(data.patientName || data.modality) && (
          <p style={{ margin: '4px 0', fontSize: '12px', opacity: 0.8 }}>
            Patient: {(data.patientName || 'Unknown').replace(/\^/g, ' ')} {data.modality ? `• ${data.modality}` : ''}
          </p>
        )}
        <button 
          onClick={() => {
            if (data.studyUID) {
              const url = generateOHIFUrl(data.studyUID);
              window.open(url, '_blank');
            } else if (data.studyId) {
              navigate(`/secure-viewer/${data.studyId}`);
            } else if (data.reportId) {
              if (window.location.pathname.includes('/radiologist')) {
                // Keep on radiologist page
              } else if (window.location.pathname.includes('/clinician')) {
                // Keep on clinician page
              } else {
                navigate('/clinician');
              }
            }
            if (onNotificationReceivedRef.current) {
              onNotificationReceivedRef.current(data);
            }
            toast.dismiss();
          }}
          style={{ 
            background: isFlagged ? '#EF4444' : (isResolved ? '#10B981' : '#3E6AE1'), 
            color: '#fff', 
            border: 'none', 
            padding: '6px 14px', 
            borderRadius: '6px',
            cursor: 'pointer',
            marginTop: '8px',
            fontSize: '13px',
            fontWeight: 500,
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = isFlagged ? '#DC2626' : (isResolved ? '#059669' : '#2d5acf')}
          onMouseOut={(e) => e.target.style.background = isFlagged ? '#EF4444' : (isResolved ? '#10B981' : '#3E6AE1')}
        >
          {isFlagged ? 'Review Concerns' : 'View Now'}
        </button>
      </div>
    );

    // Show toast with appropriate styling
    toast[isFlagged ? 'error' : (isResolved ? 'success' : (isUrgent ? 'error' : 'info'))](toastContent, {
      toastId: notifKey,
      autoClose: isFlagged ? 15000 : (isUrgent ? 12000 : 8000),
      position: 'top-right',
      style: {
        background: isFlagged ? '#FEF2F2' : (isResolved ? '#ECFDF5' : (isUrgent ? '#ffebee' : '#e3f2fd')),
        borderLeft: `4px solid ${isFlagged ? '#EF4444' : (isResolved ? '#10B981' : (isUrgent ? '#f44336' : '#3E6AE1'))}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      },
      bodyStyle: {
        fontSize: '14px'
      }
    });

    // Notify parent component
    if (onNotificationReceivedRef.current) {
      onNotificationReceivedRef.current(data);
    }
  }, [navigate]);

  useEffect(() => {
    if (!userId) {
      // Clean up if userId is removed
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // Prevent multiple connections
    if (socketRef.current?.connected) {
      return;
    }

    // Initialize Socket.io connection
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 
             process.env.REACT_APP_API_URL?.replace('/api', '') || 
             '';
    
    const newSocket = io(socketUrl, {
      auth: callback => callback({ token: localStorage.getItem('token') }),
      // Start with polling so connection always succeeds; upgrade to WebSocket if available.
      // This prevents the rapid failed-WebSocket reconnection loop when WS is unavailable.
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: maxReconnectAttempts,
      timeout: 20000,
      forceNew: false,
      autoConnect: true
    });

    // Handle connection events
    newSocket.on('connect', () => {
      console.log('Socket.io connected');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      
      // Join user-specific room only after connection is established
      if (userId) {
        newSocket.emit('join', userId);
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket.io disconnected:', reason);
      setIsConnected(false);
      
      // Only attempt manual reconnection if it wasn't intentional
      if (reason === 'io server disconnect') {
        // Server disconnected the socket, reconnect manually
        // Revoked or expired identities must sign in again; do not loop on forced disconnect.
        setIsConnected(false);
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket.io connection error:', error);
      reconnectAttemptsRef.current += 1;
      
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.warn('Max reconnection attempts reached. Socket connection failed.');
        // Optionally show a user-friendly message
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('Socket.io reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      
      // Rejoin room after reconnection
      if (userId) {
        newSocket.emit('join', userId);
      }
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Socket.io reconnection attempt', attemptNumber);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('Socket.io reconnection failed');
      setIsConnected(false);
    });

    // Listen for report completion and flagging events
    newSocket.on('REPORT_COMPLETED', handleNotification);
    newSocket.on('REPORT_FLAGGED', handleNotification);
    newSocket.on('notification', handleNotification);

    // Live study/order refresh handler
    const handleLiveStudyUpdate = (data) => {
      console.log('Real-time study/order update received:', data);
      if (onSyncCompleteRef.current) {
        onSyncCompleteRef.current(data || { studyUpdated: 1 });
      }
      if (onNotificationReceivedRef.current) {
        onNotificationReceivedRef.current(data);
      }
    };

    // Listen for real-time study lifecycle and order status changes
    newSocket.on('study-updated', handleLiveStudyUpdate);
    newSocket.on('study-claimed', handleLiveStudyUpdate);
    newSocket.on('study-unclaimed', handleLiveStudyUpdate);
    newSocket.on('radiologist-assigned', handleLiveStudyUpdate);
    newSocket.on('bulk-radiologist-assigned', handleLiveStudyUpdate);
    newSocket.on('new-study', handleLiveStudyUpdate);
    newSocket.on('order-status-changed', handleLiveStudyUpdate);

    // Listen for direct messages
    const handleDirectMessage = (data) => {
      if (!data) return;
      console.log('Direct message received via socket:', data);

      // Always dispatch event for active chat windows to receive messages
      window.dispatchEvent(new CustomEvent('new-direct-message', { detail: data }));

      const senderId = (data.sender?._id || data.sender)?.toString();
      const currentUserId = (userId?._id || userId?.id || userId)?.toString();

      // NEVER show a toast notification popup to the sender for their own outgoing message
      if (senderId && currentUserId && senderId === currentUserId) {
        return;
      }

      // If user is currently actively viewing this chat in DirectMessagesView, suppress toast popup
      if (window.__activeChatUserId && window.__activeChatUserId === senderId) {
        return;
      }

      const senderName = data.sender?.firstName
        ? `${data.sender.firstName} ${data.sender.lastName || ''}`.trim()
        : 'Someone';

      const toastId = `dm-${data._id || data.id || Date.now()}`;

      toast.info(
        <div>
          <strong style={{ display: 'block', marginBottom: '4px' }}>💬 Message from {senderName}</strong>
          <p style={{ margin: 0, fontSize: '13px' }}>{data.message?.substring(0, 80)}</p>
        </div>,
        {
          toastId, // Prevent duplicate toast popups with identical message id
          position: 'top-right',
          autoClose: 4000,
          onClick: () => {
            window.dispatchEvent(new CustomEvent('open-direct-message', { detail: { user: data.sender } }));
          }
        }
      );
    };
    newSocket.on('direct_message', handleDirectMessage);

    // Listen for direct messages read receipts in real-time
    const handleDirectMessagesRead = (data) => {
      if (!data) return;
      console.log('Direct messages read receipt received via socket:', data);
      window.dispatchEvent(new CustomEvent('direct-messages-read', { detail: data }));
    };
    newSocket.on('direct_messages_read', handleDirectMessagesRead);

    // Listen for Orthanc sync complete so frontend can refresh lists
    newSocket.on('ORTHANC_SYNC_COMPLETE', (data) => {
      console.log('ORTHANC_SYNC_COMPLETE received', data);
      if (onSyncCompleteRef.current) return onSyncCompleteRef.current(data);
      if (onNotificationReceivedRef.current) return onNotificationReceivedRef.current({ type: 'ORTHANC_SYNC_COMPLETE', ...data });
    });

    socketRef.current = newSocket;

    // Cleanup on unmount or userId change
    return () => {
      if (socketRef.current) {
        console.log('Cleaning up socket connection');
        socketRef.current.off('REPORT_COMPLETED');
        socketRef.current.off('REPORT_FLAGGED');
        socketRef.current.off('notification');
        socketRef.current.off('direct_message');
        socketRef.current.off('direct_messages_read', handleDirectMessagesRead);
        socketRef.current.off('study-updated');
        socketRef.current.off('study-claimed');
        socketRef.current.off('study-unclaimed');
        socketRef.current.off('radiologist-assigned');
        socketRef.current.off('bulk-radiologist-assigned');
        socketRef.current.off('new-study');
        socketRef.current.off('order-status-changed');
        socketRef.current.off('ORTHANC_SYNC_COMPLETE');
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('reconnect');
        socketRef.current.off('reconnect_attempt');
        socketRef.current.off('reconnect_failed');
        
        // Only disconnect if we're actually connected
        if (socketRef.current.connected) {
          socketRef.current.disconnect();
        }
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [userId, handleNotification]); // handleNotification is now stable (only depends on navigate)

  return (
    <ToastContainer
      position="top-right"
      autoClose={8000}
      hideProgressBar={false}
      newestOnTop={true}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      style={{ zIndex: 9999 }}
    />
  );
};

export default NotificationListener;

